import { FormControl, Stack, TextField, Typography } from "@mui/material";
import { format, parse } from "date-fns";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateMeMutation } from "src/api/services/patientApi";
import { usePatient } from "src/context/PatientProvider";
import EditModal from "src/shared/components/EditModal";
import {
  formatPhoneNumber,
  isValidPhoneNumber,
} from "src/utils/formatPhoneNumber";

type EditInformationModalTypes = {
  openModal: boolean;
  setOpenModal: (o: boolean) => void;
  formattedPhoneNumber: string | undefined;
};

const EditContactModal = ({
  openModal,
  setOpenModal,
  formattedPhoneNumber,
}: EditInformationModalTypes) => {
  const firstName = usePatient((patient) => patient.profile.first_name ?? "");
  const lastName = usePatient((patient) => patient.profile.last_name ?? "");
  const email = usePatient((patient) => patient.profile.email);
  const dob = usePatient((patient) => patient.profile.dob);
  const phoneNumber = usePatient((patient) => patient.profile.phone_number);
  const [updatePatient] = useUpdateMeMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    email: "",
    dob: "",
  });
  const [error, setError] = useState<string | null>(null);
  const page = "account.overview.contactCard";
  const { t } = useTranslation();

  const phoneError = useMemo(
    () =>
      t(
        `${page}.phoneError`,
        "Invalid phone number, please enter a valid number.",
      ),
    [t],
  );

  const handleOnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name!]: value,
    }));
  }, []);

  const handleOnSubmit = useCallback(async () => {
    setLoading(true);

    const validatePhoneNumber = isValidPhoneNumber(formData.phone_number, "US");

    if (!validatePhoneNumber) {
      setError(phoneError);
      setLoading(false);
      return;
    }

    try {
      await updatePatient({ profile: formData });
    } catch (err) {
      console.error("Error updating contact information", err);
    } finally {
      setOpenModal(false);
      setLoading(false);
      setError(null);
    }
  }, [formData, phoneError, setOpenModal, updatePatient]);

  useEffect(() => {
    setFormData({
      first_name: firstName,
      last_name: lastName,
      email,
      phone_number: phoneNumber || "",
      dob: dob
        ? format(parse(dob, "yyyy-MM-dd", new Date()), "yyyy-MM-dd")
        : "",
    });
  }, [formattedPhoneNumber, firstName, lastName, email, dob, phoneNumber]);

  return (
    <EditModal
      open={openModal}
      onClose={setOpenModal}
      title={t(`${page}.title`)}
      subTitle={t(`${page}.subtext`)}
      loading={loading}
      onSubmit={handleOnSubmit}
    >
      <Stack gap={3}>
        <FormControl>
          <TextField
            label={t(`${page}.first_name`)}
            name="first_name"
            id="first_name"
            value={formData?.first_name}
            onChange={handleOnChange}
          />
        </FormControl>
        <FormControl>
          <TextField
            label={t(`${page}.last_name`)}
            name="last_name"
            id="last_name"
            value={formData?.last_name}
            onChange={handleOnChange}
          />
        </FormControl>
        <FormControl>
          <TextField
            label={t(`${page}.email`)}
            name="email"
            id="email"
            disabled
            value={formData?.email}
            onChange={handleOnChange}
          />
        </FormControl>
        <FormControl>
          <TextField
            label={t(`${page}.phone`)}
            name="phone_number"
            id="phone_number"
            value={formatPhoneNumber(formData?.phone_number)}
            onChange={handleOnChange}
          />
        </FormControl>
        <FormControl>
          <TextField
            label={t(`${page}.birthday`)}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            name="dob"
            id="dob"
            type="date"
            value={formData?.dob}
            onChange={handleOnChange}
          />
        </FormControl>
        {error ? (
          <Typography sx={{ color: "red" }} textAlign="center">
            {error}
          </Typography>
        ) : null}
      </Stack>
    </EditModal>
  );
};

export default EditContactModal;
